import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, RefreshCw, Search, ShieldCheck, AlertTriangle, Layers } from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = { 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat", 7: "Sun" };

// Duplicate Session Cleanup — SCAN (read-only) → DRY-RUN → APPLY.
// Keeps the highest-enrolment copy of each duplicate slot, repoints enrolments
// & rosters onto it, and ARCHIVES the losers (never hard-deletes). Price-
// mismatched groups are flagged for manual review, never auto-merged.
export default function AdminDuplicateSessions() {
  const [terms, setTerms] = useState([]);
  const [termId, setTermId] = useState("");
  const [scan, setScan] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/academy/terms`, { withCredentials: true });
        setTerms(r.data || []);
      } catch {
        toast.error("Failed to load terms");
      }
    })();
  }, []);

  const reset = () => { setScan(null); setPlan(null); };

  const runScan = async () => {
    if (!termId) { toast.error("Select a term first"); return; }
    setLoading(true); setPlan(null);
    try {
      const r = await axios.get(
        `${API}/academy/admin/terms/${termId}/duplicate-sessions-scan`,
        { withCredentials: true }
      );
      setScan(r.data);
      if (r.data.duplicate_group_count === 0) toast.success("No duplicate sessions found for this term");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally {
      setLoading(false);
    }
  };

  const runDedupe = async (dryRun) => {
    if (!termId) return;
    dryRun ? setLoading(true) : setApplying(true);
    try {
      const r = await axios.post(
        `${API}/academy/admin/terms/${termId}/dedupe-sessions`,
        { dry_run: dryRun },
        { withCredentials: true }
      );
      setPlan(r.data);
      if (!dryRun) {
        toast.success(r.data.message || "De-dupe applied");
        await runScan();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "De-dupe failed");
    } finally {
      dryRun ? setLoading(false) : setApplying(false);
    }
  };

  const groups = scan?.groups || [];

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6" data-testid="admin-duplicate-sessions-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6" data-testid="back-to-admin-link">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Layers className="w-7 h-7 text-cyan-400" />
          <h1 className="text-3xl font-bold">Duplicate Session Cleanup</h1>
        </div>
        <p className="text-slate-400 mb-6 max-w-3xl text-sm">
          Finds session-template rows that are identical within a term (same day, time, age group,
          gender, location &amp; type) so parents no longer see the same session two or three times.
          Keeps the copy with the most enrolments, moves any enrolments &amp; rosters onto it, and
          <span className="text-cyan-300"> archives</span> the extras (never deletes). Slots whose
          <span className="text-amber-300"> price differs</span> are flagged for manual review.
        </p>

        {/* Term picker + scan */}
        <Card className="p-5 bg-slate-900/60 border border-white/10 rounded-xl mb-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm text-slate-400 mb-1">Term</label>
              <select
                data-testid="dedupe-term-select"
                value={termId}
                onChange={(e) => { setTermId(e.target.value); reset(); }}
                className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white"
              >
                <option value="">Select a term…</option>
                {terms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.is_archived || t.is_hidden ? " (archived)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={runScan}
              disabled={loading || !termId}
              data-testid="dedupe-scan-btn"
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              Scan for duplicates
            </Button>
          </div>
        </Card>

        {scan && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="dedupe-summary">
              <Card className="p-4 bg-slate-900/60 border border-white/10 rounded-xl">
                <div className="text-2xl font-bold">{scan.total_templates}</div>
                <div className="text-xs text-slate-400">Live templates</div>
              </Card>
              <Card className="p-4 bg-slate-900/60 border border-white/10 rounded-xl">
                <div className="text-2xl font-bold text-cyan-300" data-testid="dedupe-group-count">{scan.duplicate_group_count}</div>
                <div className="text-xs text-slate-400">Duplicate slots</div>
              </Card>
              <Card className="p-4 bg-slate-900/60 border border-emerald-500/30 rounded-xl">
                <div className="text-2xl font-bold text-emerald-300">{scan.total_losers_to_archive}</div>
                <div className="text-xs text-slate-400">Extras to archive</div>
              </Card>
              <Card className="p-4 bg-slate-900/60 border border-amber-500/30 rounded-xl">
                <div className="text-2xl font-bold text-amber-300">{scan.needs_review_count}</div>
                <div className="text-xs text-slate-400">Need review (price)</div>
              </Card>
            </div>

            {scan.duplicate_group_count === 0 ? (
              <Card className="p-6 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-3" data-testid="dedupe-clean">
                <ShieldCheck className="w-6 h-6 text-emerald-400" />
                <span>This term has no duplicate sessions. Nothing to clean up.</span>
              </Card>
            ) : (
              <>
                {/* Action buttons */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <Button
                    onClick={() => runDedupe(true)}
                    disabled={loading || applying}
                    data-testid="dedupe-dryrun-btn"
                    variant="outline"
                    className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
                  >
                    <Search className="w-4 h-4 mr-2" /> Dry-run (preview changes)
                  </Button>
                  <Button
                    onClick={() => {
                      if (window.confirm(`Archive ${scan.total_losers_to_archive} duplicate template(s) across ${scan.safe_to_merge_count} slot(s)? Enrolments & rosters move onto the kept copy. This is reversible (rows are archived, not deleted).`)) {
                        runDedupe(false);
                      }
                    }}
                    disabled={applying || loading || scan.safe_to_merge_count === 0}
                    data-testid="dedupe-apply-btn"
                    className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  >
                    {applying ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                    Apply de-dupe
                  </Button>
                </div>

                {plan && (
                  <Card className="p-4 bg-slate-800/60 border border-cyan-500/30 rounded-xl mb-6" data-testid="dedupe-plan">
                    <div className="font-semibold mb-2">{plan.dry_run ? "Dry-run preview" : "Applied"}</div>
                    <div className="text-sm text-slate-300 mb-2">{plan.message}</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><span className="text-slate-400">Templates archived:</span> {plan.summary.templates_archived}</div>
                      <div><span className="text-slate-400">Players repointed:</span> {plan.summary.players_repointed}</div>
                      <div><span className="text-slate-400">Rosters merged:</span> {plan.summary.rosters_merged}</div>
                      <div><span className="text-slate-400">Sessions archived:</span> {plan.summary.training_sessions_archived}</div>
                    </div>
                  </Card>
                )}

                {/* Group list */}
                <div className="space-y-3">
                  {groups.map((g, i) => (
                    <Card
                      key={i}
                      data-testid={`dedupe-group-${i}`}
                      className={`p-4 bg-slate-900/60 border rounded-xl ${g.needs_review ? "border-amber-500/40" : "border-white/10"}`}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                        <div className="font-semibold">
                          {DAYS[g.day_of_week] || `Day ${g.day_of_week}`} · {g.start_time}–{g.end_time} · {g.age_group_name} · {g.gender}
                          {g.location ? <span className="text-slate-400"> · {g.location}</span> : null}
                        </div>
                        {g.needs_review ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-300">
                            <AlertTriangle className="w-3 h-3" /> Price mismatch — review manually
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-cyan-500/20 text-cyan-300">{g.copy_count} copies</span>
                        )}
                      </div>
                      <div className="space-y-1">
                        {g.copies.map((c) => (
                          <div key={c.id} className="flex items-center justify-between text-sm bg-slate-800/50 rounded px-3 py-1.5">
                            <span className="font-mono text-xs text-slate-400">{c.id.slice(0, 8)}…</span>
                            <span>${c.price != null ? Number(c.price).toFixed(2) : "—"}</span>
                            <span className="text-slate-400">{c.enrollment_count} enrolled</span>
                            {c.is_keeper ? (
                              <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">KEEP</span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300">{g.needs_review ? "—" : "archive"}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
